export function createNavigationGuard({ authService, store, findAccessibleRoute }) {
  return async (to, from, next) => {
  // Save the intended URL for post-login redirect
  if (!["/page-login", "/auth/callback"].includes(to.path)) {
    localStorage.setItem("prevURL", to.fullPath);
  }

  try {
    // Handle Entra ID redirect callback
    if (to.path === "/auth/callback" || to.hash?.includes("access_token")) {
      console.log("🔄 MSAL callback detected:", {
        path: to.path,
        hasAccessToken: to.hash?.includes("access_token"),
        timestamp: new Date().toISOString()
      });
      try {
        console.log("🚀 Dispatching handleRedirect action to process MSAL callback...");
        const result = await store.dispatch("handleRedirect");
        if (result?.success) {
          // Load user permissions from server to ensure sidebar menu appears
          console.log("📋 Loading user permissions after redirect...");
          await store.dispatch("authpermission/loadAuthData");

          const redirectUrl =
            localStorage.getItem("prevURL") || "/dashboard";
          console.log(
            "✅ Microsoft login successful, redirecting to:",
            redirectUrl,
          );
          next({ path: redirectUrl });
          return;
        }
      } catch (error) {
        console.error("❌ Redirect handling failed:", error);
      }
    }

    // Check authentication status
    let isAuthenticated = authService.isAuthenticated();

    // Validate auth state consistency for Entra ID (prevent auth loops)
    if (isAuthenticated) {
      const authMethod = authService.getCurrentAuthMethod();
      if (authMethod === "entraId") {
        const backendToken = localStorage.getItem("jwtToken");
        if (!backendToken) {
          // Clear inconsistent state
          await authService.logout();
          isAuthenticated = false;

          // Redirect to login
          if (!["/page-login", "/auth/callback"].includes(to.path)) {
            next({ path: "/page-login" });
            return;
          }
        }
      }
    }

    // Authentication must be settled before interpreting empty permission state.
    if (to.meta.requiresAuth && !isAuthenticated) {
      return next({ path: "/page-login" });
    }

    // --- Menu-based permission check RBAC---
    // Check route permissions if specified in meta
    if (to.meta && to.meta.permissions) {
      const requiredPermissions = Array.isArray(to.meta.permissions)
        ? to.meta.permissions
        : [to.meta.permissions];

      const hasPermission = store.getters["authpermission/hasAnyPermission"](requiredPermissions);

      if (!hasPermission) {
        console.warn(`Access denied: Missing required permissions for ${to.path}`);
        console.warn(`Required permissions:`, requiredPermissions);
        console.warn(`User permissions:`, store.state.authpermission.permissions);

        // Find a route the user CAN access
        const accessibleRoute = findAccessibleRoute();

        // Prevent infinite redirect
        if (to.path === accessibleRoute) {
          console.warn(`Already navigating to accessible route ${accessibleRoute} - allowing through`);
          return next();
        }

        console.log(`Redirecting to accessible route: ${accessibleRoute}`);
        return next({ path: accessibleRoute });
      }
    }

    // Check route roles if specified in meta
    if (to.meta && to.meta.roles) {
      const requiredRoles = Array.isArray(to.meta.roles)
        ? to.meta.roles
        : [to.meta.roles];

      const hasRole = store.getters["authpermission/hasAnyRole"](requiredRoles);

      if (!hasRole) {
        console.warn(`Access denied: Missing required roles for ${to.path}`);
        console.warn(`Required roles:`, requiredRoles);
        console.warn(`User roles:`, store.state.authpermission.roles);

        // Find a route the user CAN access
        const accessibleRoute = findAccessibleRoute();

        // Prevent infinite redirect
        if (to.path === accessibleRoute) {
          console.warn(`Already navigating to accessible route ${accessibleRoute} - allowing through`);
          return next();
        }

        console.log(`Redirecting to accessible route: ${accessibleRoute}`);
        return next({ path: accessibleRoute });
      }
    }

    // Check menu access if specified in meta
    if (to.meta && to.meta.menu) {
      const isMenuAllowed = store.getters["authpermission/isMenuAllowed"](to.meta.menu);

      if (!isMenuAllowed) {
        console.warn(`Access denied: Menu item '${to.meta.menu}' not allowed for ${to.path}`);
        console.warn(`Required menu:`, to.meta.menu);
        console.warn(`Allowed menus:`, store.state.authpermission.allowedMenuItems);

        // Find a route the user CAN access
        const accessibleRoute = findAccessibleRoute();

        // Prevent infinite redirect
        if (to.path === accessibleRoute) {
          console.warn(`Already navigating to accessible route ${accessibleRoute} - allowing through`);
          return next();
        }

        console.log(`Redirecting to accessible route: ${accessibleRoute}`);
        return next({ path: accessibleRoute });
      }
    }

    if (to.meta.requiresAuth && !isAuthenticated) {
      console.log(
        "🔐 Route requires auth but user not authenticated, redirecting to login",
      );
      next({ path: "/page-login" });
    } else if (to.path === "/page-login" && isAuthenticated) {
      console.log("🏠 User already authenticated, redirecting to dashboard");
      const storedPrev = localStorage.getItem("prevURL");
      const sanitizedPrev = storedPrev ? storedPrev.split("?")[0] : null;
      const isResetFlow = [
        "/forgot-password",
        "/reset-password",
        "/recover-password",
      ].includes(sanitizedPrev);
      if (isResetFlow) {
        localStorage.removeItem("prevURL");
      }
      const redirectUrl =
        !storedPrev || isResetFlow ? "/dashboard" : storedPrev;
      next({ path: redirectUrl });
    } else {
      // Check for required scopes/roles if specified
      if (to.meta.requiredScopes && isAuthenticated) {
        const hasRequiredScopes = await authService.hasRequiredScopes(
          to.meta.requiredScopes,
        );
        if (!hasRequiredScopes) {
          console.warn(
            "User lacks required scopes:",
            to.meta.requiredScopes,
          );
          next({ path: "/unauthorized" });
          return;
        }
      }

      if (to.meta.requiredRoles && isAuthenticated) {
        const hasRequiredRoles = await authService.hasRequiredRoles(
          to.meta.requiredRoles,
        );
        if (!hasRequiredRoles) {
          console.warn(" User lacks required roles:", to.meta.requiredRoles);
          next({ path: "/unauthorized" });
          return;
        }
      }

      next(); // Proceed as normal
    }

  } catch (error) {
    console.error("Route guard error:", error);
    // Fallback to login on error
    if (to.meta.requiresAuth) {
      next({ path: "/page-login" });
    } else {
      next();
    }
  }

  };
}
